import { produce } from 'immer';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';
import { getFieldRevisions } from '../../services/fields-service';

const AsyncType = ScopedAsyncType('FIELD_AGRILYTICS');
const Type = ScopedType('FIELD_AGRILYTICS');

const SLICE = 'fieldAgrilytics';

const LOAD_FIELD_REVISIONS = AsyncType('LOAD_FIELD_REVISIONS');
const ADD_SCOPE = Type('ADD_SCOPE');
const REMOVE_SCOPE = Type('REMOVE_SCOPE');
const SET_SELECTED_FIELD = Type('SET_SELECTED_FIELD');
const SET_SELECTED_REVISION = Type('SET_SELECTED_REVISION');
const RESET_FIELD_REVISIONS = Type('RESET_FIELD_REVISIONS');

const initialState = {
  default: {
    fieldRevisions: [],
  },
};

export const selectors = {
  mapScopes: (state) => state[SLICE],
  mapScope: (scope) => (state) => state[SLICE][scope],
  fieldRevisions: (scope) => (state) => state[SLICE][scope].fieldRevisions,
  selectedRevision: (scope) => (state) => state[SLICE][scope].selectedRevision,
  selectedYear: (scope) => (state) => state[SLICE][scope].selectedYear,
  selectedField: (scope) => (state) => state[SLICE][scope].selectedField,
  activeScopes: (state) => Object.keys(state[SLICE]),
};

export const actions = {
  loadFieldRevisions,
  addScope,
  removeScope,
  setSelectedField,
  setSelectedRevision,
  resetFieldRevisions,
};

const reducerHandlers = {
  [LOAD_FIELD_REVISIONS.start]: reduceLoadFieldRevisionsStart,
  [LOAD_FIELD_REVISIONS.complete]: reduceLoadFieldRevisionsComplete,
  [LOAD_FIELD_REVISIONS.failure]: reduceLoadFieldRevisionsFailure,
  [ADD_SCOPE]: reduceAddScope,
  [REMOVE_SCOPE]: reduceRemoveScope,
  [RESET_FIELD_REVISIONS]: reduceResetFieldRevisions,
  [SET_SELECTED_FIELD]: reduceSetSelectedField,
  [SET_SELECTED_REVISION]: reduceSetSelectedRevision,
};

function loadFieldRevisions(aaid, mapScope) {
  const payload = { mapScope, aaid };
  return asyncAction(LOAD_FIELD_REVISIONS, () => getFieldRevisions(aaid), {
    startPayload: payload,
    completePayload: payload,
    failurePayload: payload,
  });
}

function addScope(scope, referenceScope) {
  const {
    selectedField,
    fieldRevisions = [],
    selectedRevision,
    selectedYear,
  } = referenceScope;

  return {
    type: ADD_SCOPE,
    payload: {
      scope,
      payload: {
        selectedField,
        fieldRevisions,
        selectedRevision,
        selectedYear,
      },
    },
  };
}

function removeScope(scope) {
  return { type: REMOVE_SCOPE, payload: scope };
}

function setSelectedField(aaid, mapScope) {
  return { type: SET_SELECTED_FIELD, payload: { aaid, mapScope } };
}

function resetFieldRevisions() {
  return { type: RESET_FIELD_REVISIONS };
}

function setSelectedRevision(revision, mapScope) {
  return { type: SET_SELECTED_REVISION, payload: { revision, mapScope } };
}

function reduceLoadFieldRevisionsStart(state, { mapScope }) {
  state[mapScope].fieldRevisions = [];
  state[mapScope].selectedRevision = null;
}

function reduceLoadFieldRevisionsComplete(state, { mapScope, payload }) {
  state[mapScope].fieldRevisions = payload;
}

function reduceLoadFieldRevisionsFailure(state, { mapScope }) {
  state[mapScope].fieldRevisions = [];
}

function reduceAddScope(state, { scope, payload }) {
  state[scope] = payload;
}

function reduceRemoveScope(state, scope) {
  delete state[scope];
}

function reduceSetSelectedField(state, { aaid, mapScope }) {
  state[mapScope].selectedField = aaid;
}

function reduceSetSelectedRevision(state, { revision, mapScope }) {
  state[mapScope].selectedRevision = revision;
}

function reduceResetFieldRevisions(state) {
  const scopes = Object.keys(state);
  scopes.forEach((scope) => {
    state[scope].fieldRevisions = [];
  });
}

export const reducers = {
  [SLICE]: (state = initialState, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
